import {useEffect, useState} from 'react';
import {useFormState} from 'react-final-form';
import {useSelector} from 'react-redux';
import {optionItemFinder} from '../helpers/OptionHelper';
import useSelectedFeature from './useSelectedFeature';
import usePrice from './usePrice';
import {APPLY_STATUS_SUCCESS} from '../actions/ApplyFieldActions';
import {COUPON_TYPE_PERCENT, COUPON_TYPE_PRICE} from '../constants/CouponTypeConstant';
import {useIsShowMap} from "./useIsShowMap";
import {isMCQTypeOfPaper} from "../helpers/TypeOfPaperHelper";

function useCalculator() {
    const {values} = useFormState();
    const selectedFeatures = useSelectedFeature();
    const showMap = useIsShowMap(values);
    const {
        academicLevel,
        typeOfPaper,
        subject,
        writerLevel,
        coefficients
    } = useSelector(state => state.orderOptions.data);
    const {coupon} = useSelector(state => state.applyField);
    const {balance} = useSelector(state => state.balance);
    const customerBalance = parseFloat(balance?.amount_value) || 0;
    const useBalance = !!values.useBalance;
    const [pagesPrice, setPagesPrice] = useState(0);
    const [price, setPrice] = useState(0);
    const [pagesOriginalPrice, setPagesOriginalPrice] = useState(0);
    const [priceOriginal, setPriceOriginal] = useState(0);
    const {priceConvert} = usePrice();

    useEffect(() => {
        const orig = calculatePagesPrice(showMap, false);
        const discounted = calculatePagesPrice(showMap);
        setPagesOriginalPrice(orig);
        setPagesPrice(discounted);
    }, [values, coupon]);

    useEffect(() => {
        const writerPrice = calculateWriterPrice();
        const featuresPrice = calculateFeaturesPrice();

        const writerNoDiscount = calculateWriterPriceWithoutDiscount(false);
        const featuresNoDiscount = calculateFeaturesPriceWithoutDiscount(false);

        const original = pagesOriginalPrice + writerNoDiscount + featuresNoDiscount;
        setPriceOriginal(original);

        let calcPrice;

        if (coupon && coupon.status === APPLY_STATUS_SUCCESS && coupon.data.type === COUPON_TYPE_PRICE) {
            const featuresFixedCoupon = selectedFeatures.reduce((featuresPrice, feature) => {
                return featuresPrice + (feature.isEditorCheck ? calculateFeaturePrice(feature) : calculateFeaturePriceWithoutDiscount(feature));
            }, 0);

            calcPrice = pagesPrice + writerNoDiscount + featuresFixedCoupon;
        } else {
            calcPrice = pagesPrice + writerPrice + featuresPrice;
        }

        setPrice(calcPrice);
    }, [values, coupon, pagesPrice, pagesOriginalPrice, selectedFeatures]);

    function calculatePagesPrice(showMap, applyDiscount = true) {
        const typeOfPaperItem = optionItemFinder(values.typeOfPaper, typeOfPaper);
        const subjectItem = optionItemFinder(values.subject, subject);
        const academicLevelItem = optionItemFinder(values.academicLevel, academicLevel);
        const pricePerPage = academicLevelItem?.deadlineDependency?.[values.deadline];

        const spacing = showMap.spacing ? parseInt(values.spacing) : 1;
        const typeOfPaperRatio = typeOfPaperItem ? parseFloat(typeOfPaperItem.ratio) : 1;
        const subjectRatio = subjectItem ? parseFloat(subjectItem.ratio) : 1;

        const isMCQ = isMCQTypeOfPaper(typeOfPaperItem);
        const pageCount = showMap.page ? (parseInt(values.page) || 0) : 0;
        const pagePrice = isMCQ ? 0 : pricePerPage * subjectRatio * typeOfPaperRatio * pageCount * spacing;

        const countTypes = {
            slide: showMap.slide ? (parseInt(values.slide) || 0) : 0,
            question: showMap.question ? (parseInt(values.question) || 0) : 0,
            problem: showMap.problem ? (parseInt(values.problem) || 0) : 0,
            visual_piece: showMap.visualPiece ? (parseInt(values.visualPiece) || 0) : 0,
        };

        const coefficientsMap = {
            slide: coefficients.slide || 0,
            question: coefficients.question || 0,
            problem: coefficients.problem || 0,
            visual_piece: coefficients.visualPiece || 0,
        };

        const calcItem = (type, count) => {
            if (count === 0) return 0;

            return pricePerPage * subjectRatio * typeOfPaperRatio * (type === 'question' ? count : coefficientsMap[type] * count);
        };

        const slidePrice = calcItem('slide', countTypes.slide);
        const questionPrice = calcItem('question', countTypes.question);
        const problemPrice = calcItem('problem', countTypes.problem);
        const visualPrice = calcItem('visual_piece', countTypes.visual_piece);

        let totalPrice = pagePrice + slidePrice + questionPrice + problemPrice + visualPrice;

        if (applyDiscount && coupon && coupon.status === APPLY_STATUS_SUCCESS) {
            if (coupon.data.type === COUPON_TYPE_PERCENT) {
                totalPrice -= totalPrice * (parseFloat(coupon.data.discount) / 100);
            } else {
                // Cap a fixed-amount coupon at the current subtotal — without
                // this, a $100 coupon on a $40 order yields totalPrice = -$60,
                // which then gets clamped to 0 elsewhere and reads to the
                // customer as "$0 due" while the backend doesn't honour it.
                totalPrice -= Math.min(parseFloat(coupon.data.discount), totalPrice);
            }
        }

        return priceConvert(totalPrice);
    }

    const calculateWriterPrice = writerLevelItem => {
        if (!writerLevelItem) {
            writerLevelItem = optionItemFinder(values.authorCategory, writerLevel);
        }
        if (!writerLevelItem) return 0;
        return pagesPrice * parseFloat(writerLevelItem.ratio || 0);
    };

    const calculateWriterPriceWithoutDiscount = (applyDiscount = true) => {
        const writerLevelItem = optionItemFinder(values.authorCategory, writerLevel);
        if (!writerLevelItem) return 0;
        const basePagesPrice = applyDiscount ? pagesPrice : pagesOriginalPrice;
        return basePagesPrice * parseFloat(writerLevelItem.ratio || 0);
    };

    const calculateFeaturesPrice = () => {
        return selectedFeatures.reduce((featuresPrice, feature) => {
            const val = calculateFeaturePrice(feature);
            return featuresPrice + val;
        }, 0);
    };

    const calculateFeaturePrice = (featureItem) => {
        if (featureItem.price === 0) {
            return 0.00;
        } else if (featureItem.isEditorCheck) {
            const base = getPagesOnlyPrice(showMap);
            const ratio = featureItem.price;
            return base * ratio;
        } else {
            return featureItem.price ? featureItem.price : parseFloat(featureItem.ratio || 0);
        }
    };

    const calculateFeaturesPriceWithoutDiscount = (applyDiscount = true) => {
        return selectedFeatures.reduce((featuresPrice, feature) => {
            const val = calculateFeaturePriceWithoutDiscount(feature, applyDiscount);
            return featuresPrice + val;
        }, 0);
    };

    const calculateFeaturePriceWithoutDiscount = (featureItem, applyDiscount = true) => {
        if (featureItem.isEditorCheck) {
            return getPagesOnlyPrice(showMap, applyDiscount) * parseFloat(featureItem.ratio || 0);
        }
        return featureItem.price;
    };

    const calculateWriterPricePerPage = writerLevelItem => {
        return calculateWriterPrice(writerLevelItem) / (parseInt(values.page) || 1);
    };

    const getPagesOnlyPrice = (showMap, applyDiscount = true) => {
        const typeOfPaperItem = optionItemFinder(values.typeOfPaper, typeOfPaper);
        const subjectItem = optionItemFinder(values.subject, subject);
        const academicLevelItem = optionItemFinder(values.academicLevel, academicLevel);
        const pricePerPage = parseFloat(academicLevelItem?.deadlineDependency?.[values.deadline] || 0);
        const spacing = showMap.spacing ? parseInt(values.spacing) : 1;
        const typeOfPaperRatio = typeOfPaperItem ? parseFloat(typeOfPaperItem.ratio) : 1;
        const subjectRatio = subjectItem ? parseFloat(subjectItem.ratio) : 1;
        const isMCQ = isMCQTypeOfPaper(typeOfPaperItem);
        const pageCount = showMap.page ? (parseInt(values.page) || 0) : 0;

        if (isMCQ || pageCount === 0) return 0;

        let pagePrice = pricePerPage * typeOfPaperRatio * subjectRatio * pageCount * spacing;

        if (applyDiscount && coupon && coupon.status === APPLY_STATUS_SUCCESS) {
            if (coupon.data.type === COUPON_TYPE_PERCENT) {
                pagePrice -= pagePrice * (parseFloat(coupon.data.discount) / 100);
            } else {
                // Cap a fixed-amount coupon — same rule the main pages-price
                // calculation uses. Without this the editor-check ratio
                // recompute (which calls this) can drive itself negative on
                // an over-sized PRICE coupon.
                pagePrice -= Math.min(parseFloat(coupon.data.discount), pagePrice);
            }
        }

        return priceConvert(pagePrice);
    };

    const calculateOriginalWriterPricePerPage = writerLevelItem => {
        const originalPagesPrice = calculatePagesPrice(showMap, false);
        return (originalPagesPrice * parseFloat(writerLevelItem.ratio || 0)) / (parseInt(values.page) || 1);
    };

    const calculateOriginalFeaturePrice = (featureItem) => {
        if (featureItem.isEditorCheck) {
            return getPagesOnlyPrice(showMap, false) * featureItem.price;
        } else {
            return featureItem.price ? featureItem.price : parseFloat(featureItem.ratio || 0);
        }
    };

    // Store credit applied on this order — capped at the post-coupon
    // total so we never overdraw what the customer actually owes. Backend
    // mirrors the same rule when the order is created with use_balance=1.
    const balanceApplied = useBalance && customerBalance > 0
        ? Math.min(customerBalance, price)
        : 0;
    const priceAfterBalance = Math.max(0, price - balanceApplied);

    return {
        price,
        priceOriginal,
        balanceApplied,
        priceAfterBalance,
        customerBalance,
        calculateWriterPrice,
        calculateWriterPricePerPage,
        calculateFeaturePrice,
        calculateOriginalWriterPricePerPage,
        calculateOriginalFeaturePrice
    };
}

export default useCalculator;
